import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { fetchUser } from '../../store/slices/authSlice';
import { fetchLoveShots } from '../../store/slices/loveshotsSlice';
import { fetchConversations } from '../../store/slices/messagesSlice';
import { dashboardAPI } from '../../services/api';

import LoveShotCard from '../../components/features/loveshots/LoveShotCard';
import PageHeader from '../../components/common/PageHeader';
import {
  FaHeart,
  FaComment,
  FaEye,
  FaUser,
  FaCalendarAlt,
  FaMapMarkerAlt,
  FaCamera,
  FaSpinner,
  FaBell,
  FaCog
} from 'react-icons/fa';
import './Dashboard.css';

const Dashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);

  const { items: loveshots, loading: loveshotsLoading } = useSelector((state) => state.loveshots);
  const { conversations, unreadCount } = useSelector((state) => state.messages);

  const [stats, setStats] = useState({
    loveshots: 0,
    likes: 0,
    comments: 0,
    views: 0,
    followers: 0,
    following: 0
  });

  const [activity, setActivity] = useState([]);
  const [statsLoading, setStatsLoading] = useState(false);

  useEffect(() => {
    dispatch(fetchUser());
    dispatch(fetchLoveShots({ page: 1, userId: user?.id }));
    dispatch(fetchConversations());

    // Fetch real stats from API
    const fetchStats = async () => {
      try {
        setStatsLoading(true);
        const response = await dashboardAPI.getStats();
        setStats(response.data);
      } catch (error) {
        console.error('Error fetching stats:', error);
        // Keep default values on error
      } finally {
        setStatsLoading(false);
      }
    };

    // Fetch activity feed
    const fetchActivity = async () => {
      try {
        const response = await dashboardAPI.getActivity({ limit: 10 });
        setActivity(response.data);
      } catch (error) {
        console.error('Error fetching activity:', error);
        // Keep empty array on error
      }
    };

    if (user?.id) {
      fetchStats();
      fetchActivity();
    }
  }, [dispatch, user?.id]);

  const recentLoveshots = loveshots.slice(0, 3);
  const recentConversations = conversations.slice(0, 3);

  return (
    <div className="dashboard-page">
      <div className="dashboard-container">
        {/* Welcome Header */}
        <PageHeader
          title={`Bonjour ${user?.username} ! 👋`}
          subtitle="Bienvenue sur votre tableau de bord CHATOO"
          button={{
            text: "Nouveau LoveShot",
            icon: FaCamera,
            onClick: () => console.log('Create LoveShot')
          }}
        />

        {/* Stats Cards */}
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon">
              <FaCamera />
            </div>
            <div className="stat-info">
              <h3>{stats.loveshots}</h3>
              <p>LoveShots</p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">
              <FaHeart />
            </div>
            <div className="stat-info">
              <h3>{stats.likes}</h3>
              <p>Likes reçus</p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">
              <FaComment />
            </div>
            <div className="stat-info">
              <h3>{stats.comments}</h3>
              <p>Commentaires</p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">
              <FaEye />
            </div>
            <div className="stat-info">
              <h3>{stats.views}</h3>
              <p>Vues</p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">
              <FaUser />
            </div>
            <div className="stat-info">
              <h3>{stats.followers}</h3>
              <p>Followers</p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">
              <FaUser />
            </div>
            <div className="stat-info">
              <h3>{stats.following}</h3>
              <p>Suivis</p>
            </div>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="dashboard-grid">
          {/* Recent LoveShots */}
          <div className="dashboard-section">
            <div className="section-header">
              <h2>Vos derniers LoveShots</h2>
              <button className="see-all-btn">Voir tout</button>
            </div>

            {loveshotsLoading ? (
              <div className="loading-container">
                <FaSpinner className="spinner" />
                <p>Chargement...</p>
              </div>
            ) : recentLoveshots.length > 0 ? (
              <div className="recent-loveshots">
                {recentLoveshots.map((loveshot) => (
                  <LoveShotCard key={loveshot.id} loveshot={loveshot} compact />
                ))}
              </div>
            ) : (
              <div className="empty-section">
                <FaCamera className="empty-icon" />
                <h3>Aucun LoveShot</h3>
                <p>Partagez votre premier LoveShot pour commencer !</p>
                <button className="action-btn primary">Créer un LoveShot</button>
              </div>
            )}
          </div>

          {/* Recent Messages */}
          <div className="dashboard-section">
          <div className="section-header">
            <h2>Conversations récentes</h2>
            <button className="see-all-btn" onClick={() => navigate('/messages')}>Voir tout</button>
          </div>


            {recentConversations.length > 0 ? (
              <div className="recent-conversations">
                {recentConversations.map((conversation) => {
                  const otherParticipant = conversation.participants.find(p => p.id !== user?.id);
                  return (
                    <div 
                      key={conversation.id} 
                      className="conversation-preview"
                      onClick={() => navigate(`/messages?conversation=${conversation.id}`)}
                      style={{ cursor: 'pointer' }}
                    >
                      <img
                        src={otherParticipant?.profile?.photo_url || otherParticipant?.profile?.profile_picture || otherParticipant?.photo_url || '/default-avatar.svg'}
                        alt={otherParticipant?.username}
                        className="conversation-avatar"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = '/default-avatar.svg';
                        }}
                      />

                      <div className="conversation-info">
                        <h4>{otherParticipant?.first_name} {otherParticipant?.last_name}</h4>
                        <p className="last-message">
                          {conversation.last_message?.content || 'Aucun message'}
                        </p>
                        <span className="message-times">
                          {conversation.updated_at ? new Date(conversation.updated_at).toLocaleDateString('fr-FR') : ''}
                        </span>
                      </div>
                      {conversation.unread_count > 0 && (
                        <span className="unread-badge">{conversation.unread_count}</span>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="empty-section">
                <FaComment className="empty-icon" />
                <h3>Aucune conversation</h3>
                <p>Découvrez de nouvelles personnes et commencez à discuter !</p>
                <button className="action-btn primary">Explorer</button>
              </div>
            )}
          </div>

          {/* Activity Feed */}
          <div className="dashboard-section full-width">
            <div className="section-header">
              <h2>Activité récente</h2>
            </div>

            <div className="activity-feed">
              {activity.length > 0 ? (
                activity.map((item) => {
                  const getIcon = (type) => {
                    switch (type) {
                      case 'loveshot_liked':
                        return <FaHeart />;
                      case 'loveshot_commented':
                        return <FaComment />;
                      case 'user_followed':
                        return <FaUser />;
                      case 'loveshot_viewed':
                        return <FaEye />;
                      default:
                        return <FaBell />;
                    }
                  };

                  return (
                    <div key={item.id} className="activity-item">
                      <div className="activity-icon">
                        {getIcon(item.type)}
                      </div>
                      <div className="activity-content">
                        <p>{item.message}</p>
                        <span className="activity-time">{item.time}</span>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="empty-section">
                  <FaBell className="empty-icon" />
                  <h3>Aucune activité récente</h3>
                  <p>Vos activités apparaîtront ici.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
